import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, Card, PageHeader, Select, Table, Tabs } from '../../design';
import { fmtDate } from '../../lib/format';
import { ALERT_LEVEL, ALERT_TYPE } from '../alerts/AlertsPage';
import { ActivityChart } from '../dashboard/ActivityChart';
import { STATUS_COLOR, StatusBadge, TYPE_LABEL } from '../lockers/common';
import { Kpi } from '../operations/OperationsPage';
import os from '../operations/operations.module.css';
import { useOrgOptions } from '../users/UsersPage';
import { ExportButton } from './ExportButton';
import s from './reports.module.css';

type OperationsReport = components['schemas']['OperationsReport'];
type OccupancyReport = components['schemas']['OccupancyReport'];
type AlertsReport = components['schemas']['AlertsReport'];
type LockerType = components['schemas']['LockerType'];
type Days = '7' | '14' | '30';

/** Începutul zilei locale de acum `days − 1` zile: intervalul include ziua curentă. */
export function rangeFor(days: number, now: Date): { from: string; to: string } {
  const from = new Date(now);
  from.setDate(now.getDate() - (days - 1));
  from.setHours(0, 0, 0, 0);
  return { from: from.toISOString(), to: now.toISOString() };
}

type ReportScope = { from: string; to: string; orgId?: string };

function useOperationsReport(scope: ReportScope, groupBy: 'day' | 'locker' | 'type') {
  return useQuery({
    queryKey: ['reports', 'operations', groupBy, scope],
    queryFn: () =>
      unwrap(api.GET('/reports/operations', { params: { query: { ...scope, groupBy } } })),
  });
}

/** Rapoarte (spec §4.4, D7): agregări pe server pe 7 / 14 / 30 zile, ocupare, alerte și exporturi CSV. */
export function ReportsPage() {
  const { user } = useAuth();
  const orgs = useOrgOptions();
  const [days, setDays] = useState<Days>('30');
  const [orgId, setOrgId] = useState('');
  const [now] = useState(() => new Date());
  const range = useMemo(() => rangeFor(Number(days), now), [days, now]);
  const scope = { ...range, ...(orgId ? { orgId } : {}) };

  const byDay = useOperationsReport(scope, 'day');
  const byType = useOperationsReport(scope, 'type');
  const byLocker = useOperationsReport(scope, 'locker');
  const occupancy = useQuery({
    queryKey: ['reports', 'occupancy', orgId],
    queryFn: () =>
      unwrap(
        api.GET('/reports/occupancy', { params: { query: orgId ? { orgId } : {} } }),
      ) as Promise<OccupancyReport>,
  });
  const alerts = useQuery({
    queryKey: ['reports', 'alerts', scope],
    queryFn: () =>
      unwrap(api.GET('/reports/alerts', { params: { query: scope } })) as Promise<AlertsReport>,
  });

  const totals = byDay.data?.totals;
  const chartDays = (byDay.data?.groups ?? []).map((g) => ({
    label: g.label,
    created: g.created,
    done: g.done,
  }));
  const exportParams = { from: range.from, to: range.to, ...(orgId ? { orgId } : {}) };

  return (
    <>
      <PageHeader
        title="Rapoarte"
        subtitle="Agregări calculate pe server, în fusul României; exporturile CSV respectă filtrele și perimetrul."
      />
      <div className={s.toolbar}>
        <Tabs<Days>
          value={days}
          onChange={setDays}
          items={[
            { value: '7', label: '7 zile' },
            { value: '14', label: '14 zile' },
            { value: '30', label: '30 zile' },
          ]}
        />
        <span className="small muted">
          {fmtDate(range.from)} – {fmtDate(range.to)}
        </span>
        {user?.orgId === null && (
          <Select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            style={{ maxWidth: 260 }}
            aria-label="Organizație"
          >
            <option value="">Toate organizațiile</option>
            {orgs.data?.items.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        )}
      </div>
      {byDay.error && <p style={{ color: 'var(--red)' }}>{errorMessage(byDay.error)}</p>}
      <div className={os.kpis}>
        <Kpi label="Operațiuni create" value={totals?.created} hint="În intervalul ales" />
        <Kpi label="Finalizate" value={totals?.done} hint="Ridicări confirmate" tone="green" />
        <Kpi
          label="Expirate"
          value={totals?.expired}
          hint="Termen de depunere depășit"
          tone="red"
        />
        <Kpi label="Anulate" value={totals?.cancelled} hint="Din consolă sau API" tone="orange" />
        <Kpi
          label="Durată medie (h)"
          value={totals === undefined ? undefined : (totals.avgCompletionHours ?? 0)}
          hint={
            totals?.avgCompletionHours === null
              ? 'Nicio finalizare în interval'
              : 'De la creare la ridicare'
          }
        />
      </div>

      <div className={s.grid2}>
        <Card>
          <h2>Activitate zilnică</h2>
          <p className="small muted">Operațiuni create și finalizate, pe zile.</p>
          {chartDays.length > 0 && <ActivityChart days={chartDays} />}
        </Card>
        <Card>
          <h2>Volum pe scenariu</h2>
          <p className="small muted">Operațiuni create, după tipul lockerului.</p>
          <ScenarioBars report={byType.data} />
        </Card>
      </div>

      <Card tight style={{ marginBottom: 16 }}>
        <div style={{ padding: 16 }}>
          <h2>Performanță pe echipament</h2>
          <p className="small muted">Fiecare locker cu activitate în interval.</p>
        </div>
        <Table<OperationsReport['groups'][number]>
          columns={[
            {
              key: 'locker',
              header: 'Locker',
              render: (g) => (
                <Link to={`/lockere/${g.key}`}>
                  <strong>{g.label}</strong>
                </Link>
              ),
            },
            { key: 'created', header: 'Create', align: 'right', render: (g) => g.created },
            { key: 'done', header: 'Finalizate', align: 'right', render: (g) => g.done },
            { key: 'expired', header: 'Expirate', align: 'right', render: (g) => g.expired },
            { key: 'cancelled', header: 'Anulate', align: 'right', render: (g) => g.cancelled },
            {
              key: 'avg',
              header: 'Durată medie (h)',
              align: 'right',
              render: (g) => (g.avgCompletionHours === null ? '—' : g.avgCompletionHours),
            },
          ]}
          rows={byLocker.data?.groups ?? []}
          rowKey={(g) => g.key}
          empty={byLocker.isLoading ? 'Se încarcă…' : 'Nicio operațiune în interval.'}
        />
      </Card>

      <div className={s.grid2}>
        <Card tight>
          <div style={{ padding: 16 }}>
            <h2>Ocupare</h2>
            <p className="small muted">
              Starea curentă a compartimentelor; cele blocate tehnic nu intră în procent.
              {occupancy.data && (
                <>
                  {' '}
                  Total: {occupancy.data.totals.total} compartimente, {occupancy.data.totals.free}{' '}
                  libere, {occupancy.data.totals.occupied} ocupate, {occupancy.data.totals.reserved}{' '}
                  rezervate, {occupancy.data.totals.blocked} blocate.
                </>
              )}
            </p>
          </div>
          <Table<OccupancyReport['lockers'][number]>
            columns={[
              {
                key: 'locker',
                header: 'Locker',
                render: (l) => (
                  <>
                    <Link to={`/lockere/${l.lockerId}`}>
                      <strong>{l.name}</strong>
                    </Link>
                    <div className="small muted">
                      {TYPE_LABEL[l.type]} · {l.city}
                    </div>
                  </>
                ),
              },
              { key: 'status', header: 'Stare', render: (l) => <StatusBadge status={l.status} /> },
              {
                key: 'occ',
                header: 'Ocupare',
                render: (l) => (
                  <>
                    <span className="small">
                      {l.occupancyPct}% · {l.slots.occupied + l.slots.reserved}/
                      {l.slots.total - l.slots.blocked}
                    </span>
                    <div className={s.meter} aria-hidden="true">
                      <i
                        style={{
                          width: `${l.occupancyPct}%`,
                          background:
                            l.occupancyPct >= 90 ? STATUS_COLOR.offline : STATUS_COLOR.online,
                        }}
                      />
                    </div>
                  </>
                ),
              },
            ]}
            rows={occupancy.data?.lockers ?? []}
            rowKey={(l) => l.lockerId}
            empty={occupancy.isLoading ? 'Se încarcă…' : 'Niciun locker în perimetru.'}
          />
        </Card>
        <Card>
          <h2>Alerte</h2>
          <p className="small muted">
            Ridicate și rezolvate în interval; timpul mediu până la rezolvare.
          </p>
          {alerts.data && (
            <>
              <div className={s.levels}>
                <div>
                  <span className="small muted">Deschise acum</span>
                  <strong>{alerts.data.totals.open}</strong>
                </div>
                {alerts.data.byLevel.map((l) => (
                  <div key={l.level}>
                    <Badge tone={ALERT_LEVEL[l.level].tone}>{ALERT_LEVEL[l.level].label}</Badge>
                    <strong>{l.raised}</strong>
                    <span className="small muted">{l.resolved} rezolvate</span>
                  </div>
                ))}
                <div>
                  <span className="small muted">Rezolvare medie</span>
                  <strong>
                    {alerts.data.totals.avgResolutionHours === null
                      ? '—'
                      : `${alerts.data.totals.avgResolutionHours} h`}
                  </strong>
                </div>
              </div>
              <Table<AlertsReport['byType'][number]>
                columns={[
                  { key: 'type', header: 'Tip', render: (t) => ALERT_TYPE[t.type] },
                  { key: 'raised', header: 'Ridicate', align: 'right', render: (t) => t.raised },
                  {
                    key: 'resolved',
                    header: 'Rezolvate',
                    align: 'right',
                    render: (t) => t.resolved,
                  },
                  {
                    key: 'avg',
                    header: 'Rezolvare medie (h)',
                    align: 'right',
                    render: (t) => (t.avgResolutionHours === null ? '—' : t.avgResolutionHours),
                  },
                ]}
                rows={alerts.data.byType}
                rowKey={(t) => t.type}
                empty="Nicio alertă în interval."
              />
            </>
          )}
        </Card>
      </div>

      <Card>
        <h2>Exporturi CSV</h2>
        <p className="small muted">
          UTF-8 cu BOM și separator „;” (Excel în română), maximum 50 000 de rânduri per fișier;
          intervalul și organizația de mai sus se aplică unde au sens. Cheile se exportă fără
          coduri. Istoricul stărilor lockerelor se acumulează pentru un viitor raport de
          disponibilitate.
        </p>
        <div className="flex wrap" style={{ marginTop: 12 }}>
          <ExportButton kind="operations" params={exportParams}>
            Operațiuni
          </ExportButton>
          <ExportButton kind="access-grants" params={exportParams}>
            Chei de acces
          </ExportButton>
          <ExportButton kind="alerts" params={exportParams}>
            Alerte
          </ExportButton>
          <ExportButton kind="audit" params={exportParams}>
            Jurnal de audit
          </ExportButton>
          <ExportButton kind="lockers" params={orgId ? { orgId } : {}}>
            Lockere
          </ExportButton>
        </div>
      </Card>
    </>
  );
}

function ScenarioBars({ report }: { report: OperationsReport | undefined }) {
  if (!report) return <p className="small muted">Se încarcă…</p>;
  if (report.groups.length === 0)
    return <p className="small muted">Nicio operațiune în interval.</p>;
  const max = Math.max(1, ...report.groups.map((g) => g.created));
  return (
    <div className={s.bars} role="list" aria-label="Volum pe scenariu">
      {report.groups.map((g) => (
        <div key={g.key} className={s.bar} role="listitem">
          <span>{TYPE_LABEL[g.key as LockerType] ?? g.key}</span>
          <div className={s.track}>
            <i style={{ width: `${Math.round((g.created / max) * 100)}%` }} />
          </div>
          <b>{g.created}</b>
        </div>
      ))}
    </div>
  );
}
